import React from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { connect } from 'react-redux';
import { Alert, Form, Input, message, Modal, Rate, Tag } from 'antd';
import { getReviewTags, submitReview } from '../../thunks/work_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	supplierFacilityReviewsRestCrudThunksForBuyer,
	supplierFacilityReviewsRestCrudThunksForSupplier,
} from '../../thunks/supplier_facility_reviews_thunks';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../../actions/work_orders_actions';
import ClearErrors from '../clear_errors/ClearErrors';

const NEGATIVETHRESHOLD = 4;
const RATING_LANGUAGE = {
	5: 'Great',
	4: 'Good, but not great',
	3: 'Ok, needs improvement',
	2: 'Poor',
	1: 'Terrible',
};
const OTHER_TAG_ID = [8, 16];

function WorkOrderReviewComponent({
	workOrder,
	fetchReviewTags,
	onCancel,
	createErrors,
	updateErrors,
	currentUser,
	submitRating,
	updateRating,
	successCb,
	formData = {} as { id?: number },
}) {
	const [form] = Form.useForm();
	const [masterTags, setMasterTags] = React.useState([]);
	const [selectedTags, setSelectedTags] = React.useState<number[]>(
		nullSafeGet('reviewTagIds', formData) || []
	);
	const [loading, setLoading] = React.useState(false);

	const isAssignedToThirdParty = React.useMemo(() => {
		return nullSafeGetOrElse('supplierFacility.status', workOrder, 'active') !== 'private';
	}, [workOrder]);

	React.useEffect(() => {
		if (isAssignedToThirdParty) {
			fetchReviewTags({ offset: 0, limit: 20 }).then((data = []) => {
				if (data.length) {
					setMasterTags(data);
				}
			});
		}
	}, [fetchReviewTags, isAssignedToThirdParty]);

	const noteRequired = React.useMemo(() => {
		// If only one tag is selected and it's one of OTHER_TAG_ID, review is required
		return selectedTags.length === 1 && OTHER_TAG_ID.includes(selectedTags[0]);
	}, [selectedTags]);

	const toggleTag = React.useCallback(
		(tagId: number) => {
			const updatedTags = selectedTags.includes(tagId)
				? selectedTags.filter((id) => id !== tagId)
				: [...selectedTags, tagId];
			setSelectedTags(updatedTags);
			form.setFieldsValue({ tags: updatedTags });
		},
		[selectedTags, form]
	);
	if (!isAssignedToThirdParty)
		return (
			<Form.Item label="Review" name="note">
				<Input.TextArea placeholder="Share a few words about your experience" />
			</Form.Item>
		);
	return (
		<Modal
			visible
			width={600}
			title={'How would you rate the service provider?'}
			okText={'Submit'}
			onCancel={onCancel}
			onOk={() => form.submit()}
			closable={false}
			confirmLoading={loading}
		>
			<Form
				layout="vertical"
				className="supplierForm"
				form={form}
				onFinish={(values) => {
					if (values.rating) {
						const action = formData.id ? updateRating : submitRating;
						setLoading(true);
						const reviewValue = noteRequired ? values.review : '';
						action({
							...values,
							workOrderId: workOrder.id,
							supplierFacilityId: nullSafeGet('supplierFacilityId', workOrder),
							buyerContactEmail: nullSafeGet('email', currentUser),
							...formData,
							reviewTagIds: values.tags || [],
							review: reviewValue || '',
							rating: values.rating,
						}).then(() => {
							if (formData.id) {
								message.success('Review updated successfully');
							}
							successCb({ ...values, workOrderId: workOrder.id });
						});
					} else {
						successCb({ ...values, workOrderId: workOrder.id });
					}
				}}
				initialValues={{ ...formData }}
			>
				<ClearErrors actionCreators={WORK_ORDERS_CRUD_ACTION_CREATORS} />
				{createErrors.length > 0 && (
					<Form.Item>
						<Alert message={createErrors.join(' ')} type="error" />
					</Form.Item>
				)}
				{updateErrors.length > 0 && (
					<Form.Item>
						<Alert message={updateErrors.join(' ')} type="error" />
					</Form.Item>
				)}

				<div className="flex flex-col items-center">
					<h2 className="text-xl font-bold">
						{form.getFieldValue('rating') ? RATING_LANGUAGE[form.getFieldValue('rating')] : ``}
					</h2>
					<Form.Item label="" name="rating">
						<Rate
							style={{ fontSize: 40 }}
							onChange={(value) => {
								form.setFieldsValue({ tags: [] });
								setSelectedTags([]);
								form.setFieldsValue({ review: '' });
							}}
						/>
					</Form.Item>
					{(masterTags.length > 0 && form.getFieldValue('rating') && (
						<Form.Item
							label=""
							name="tags"
							initialValue={selectedTags}
							rules={[
								{
									required: true,
									message: 'Please select at least one option that describes your experience.',
								},
							]}
							style={{ width: '80%' }}
						>
							<div className="flex flex-row flex-wrap items-center justify-center gap-y-3">
								{masterTags
									.filter((tag) =>
										form.getFieldValue('rating') > NEGATIVETHRESHOLD
											? tag.isPositiveSentiment
											: !tag.isPositiveSentiment
									)
									.map((tag) => (
										<Tag
											key={tag.id}
											className={`${
												selectedTags.includes(tag.id) ? 'ant-tag-checkable-checked' : ''
											}`}
											style={{
												cursor: 'pointer',
												padding: '6px',
											}}
											onClick={() => toggleTag(tag.id)}
										>
											{tag.title}
										</Tag>
									))}
							</div>
						</Form.Item>
					)) ||
						null}
					{form.getFieldValue('rating') && noteRequired ? (
						<Form.Item
							style={{ width: '80%' }}
							label=""
							name="review"
							rules={[
								{
									required: true,
									message: 'Please describe your experience when selecting "Other"',
								},
							]}
						>
							<Input.TextArea placeholder="Share a few words about your experience" />
						</Form.Item>
					) : null}
				</div>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state, ownProps) => ({
	createErrors: state.work_orders.createErrors,
	updateErrors: state.work_orders.updateErrors,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchReviewTags: (params) => dispatch(getReviewTags(ownProps.userType)(params)),
	submitRating: (entity) => dispatch(submitReview(ownProps.userType)(entity)),
	updateRating: (entity) =>
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? dispatch(supplierFacilityReviewsRestCrudThunksForSupplier.update(entity))
			: dispatch(supplierFacilityReviewsRestCrudThunksForBuyer.update(entity)),
});

const ReviewComponent = connect(mapStateToProps, mapDispatchToProps)(WorkOrderReviewComponent);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ReviewComponent);
